/**
 * CareSop 数据适配器
 * 用于处理用户端和 Admin 端不同的数据结构
 */

import type {
  CareSopInstance,
  CareSopInstanceStep,
  CareSopInstanceAdminDetail,
} from "@/types/care-sop";

/**
 * Admin 端数据结构
 */
interface AdminCareSopData {
  id: number;
  status: "active" | "completed" | "abandoned";
  created_at: string;
  updated_at: string;
  progress_display: string;
  user: {
    id: number;
    email: string;
    name?: string;
  };
  disease: {
    code?: string;
    name?: string;
  };
  assessment: {
    id: number;
    friendly_id: string;
    water_type: "freshwater" | "saltwater";
  };
  tank_context?: {
    volume?: number;
    water_type?: "freshwater" | "saltwater";
    name?: string;
  };
  template?: {
    id: number;
    disease_code: string;
    disease_name: string;
  };
  steps: CareSopInstanceStep[];
  medical_info?: {
    root_causes?: string;
    prognosis?: string;
    prevention?: string;
  };
}

/**
 * 将 Admin 端数据结构转换为用户端数据结构
 */
export function adaptAdminToUserCareSop(
  adminData: AdminCareSopData,
): CareSopInstance {
  // 提取水体类型：优先使用 tank_context，其次使用 assessment
  const waterType =
    adminData.tank_context?.water_type || adminData.assessment.water_type;

  // 提取病害信息：优先使用 template，其次使用 disease
  const diseaseCode =
    adminData.template?.disease_code || adminData.disease?.code;
  const diseaseName =
    adminData.template?.disease_name || adminData.disease?.name;

  return {
    id: adminData.id,
    assessment_id: adminData.assessment.id,
    sop_template_id: adminData.template?.id,
    status: adminData.status,
    water_type: waterType || "freshwater",
    disease_code: diseaseCode,
    disease_name: diseaseName,
    created_at: adminData.created_at,
    updated_at: adminData.updated_at,
    progress_display: adminData.progress_display,
    steps: adminData.steps,
  };
}

/**
 * 判断是否为 Admin 端数据结构
 */
export function isAdminCareSopData(data: any): data is AdminCareSopData {
  return (
    data != null &&
    typeof data === "object" &&
    "assessment" in data &&
    typeof data.assessment === "object" &&
    "water_type" in data.assessment &&
    "disease" in data &&
    typeof data.disease === "object"
  );
}

/**
 * 统一获取 CareSop 数据
 * 自动适配不同的数据结构
 */
export function getUnifiedCareSop(data: any): CareSopInstance {
  if (isAdminCareSopData(data)) {
    return adaptAdminToUserCareSop(data);
  }

  // 假设已经是用户端数据结构
  return data as CareSopInstance;
}

/**
 * 获取水体类型
 */
export function getWaterType(data: any): "freshwater" | "saltwater" {
  if (isAdminCareSopData(data)) {
    return (
      data.tank_context?.water_type ||
      data.assessment.water_type ||
      "freshwater"
    );
  }

  // 用户端数据结构
  return (data as CareSopInstance).water_type || "freshwater";
}

/**
 * 获取病害名称
 */
export function getDiseaseName(data: any): string | undefined {
  if (isAdminCareSopData(data)) {
    return data.template?.disease_name || data.disease?.name;
  }

  // 用户端数据结构
  return (data as CareSopInstance).disease_name;
}

/**
 * 获取病害代码
 */
export function getDiseaseCode(data: any): string | undefined {
  if (isAdminCareSopData(data)) {
    return data.template?.disease_code || data.disease?.code;
  }

  // 用户端数据结构
  return (data as CareSopInstance).disease_code;
}
